import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
} from '@mui/material';
import {
  Add,
  Delete,
  PlayArrow,
  Groups,
  ArrowBack,
  PersonAdd,
  OpenInNew,
} from '@mui/icons-material';
import { useNotification } from '../contexts/NotificationContext';
import { teamsApi, usersApi, InternalProfile } from '../services/api';
import { Team, TeamMember, TeamDiscoveryResult, StrategyTypeKey, QuadrantKey } from '../types';
import TeamMap from '../components/TeamMap';
import TeamTypeCard from '../components/TeamTypeCard';

const STRATEGY_OPTIONS: { key: StrategyTypeKey; label: string }[] = [
  { key: 'exploring', label: 'Exploring' },
  { key: 'pathfinding', label: 'Pathfinding' },
  { key: 'producing', label: 'Producing' },
  { key: 'executing', label: 'Executing' },
  { key: 'stabilizing', label: 'Stabilizing' },
  { key: 'anchoring', label: 'Anchoring' },
  { key: 'cultivating', label: 'Cultivating' },
  { key: 'bolstering', label: 'Bolstering' },
  { key: 'adapting', label: 'Adapting' },
  { key: 'balancing', label: 'Balancing' },
];

const QUADRANT_KEYS: QuadrantKey[] = [
  'innovation_agility',
  'results_discipline',
  'process_precision',
  'teamwork_experience',
];

const QUADRANT_LABELS: Record<QuadrantKey, string> = {
  innovation_agility: 'Innovation & Agility',
  results_discipline: 'Results & Discipline',
  process_precision: 'Process & Precision',
  teamwork_experience: 'Teamwork & Employee Experience',
};

const parseNumber = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : null;
};

const toSafePercent = (value: unknown): number => {
  const parsed = parseNumber(value);
  if (parsed == null) return 0;
  return Math.max(0, Math.min(100, parsed));
};

const hasCompletePiFactors = (source: Partial<TeamMember> | InternalProfile): boolean => (
  parseNumber(source.pi_factor_a) != null &&
  parseNumber(source.pi_factor_b) != null &&
  parseNumber(source.pi_factor_c) != null &&
  parseNumber(source.pi_factor_d) != null
);

const isLikelyEstimatedProfileScores = (source: Partial<TeamMember> | InternalProfile): boolean => {
  if (!source.reference_profile) return false;
  const factors = [
    parseNumber(source.pi_factor_a),
    parseNumber(source.pi_factor_b),
    parseNumber(source.pi_factor_c),
    parseNumber(source.pi_factor_d),
  ];
  if (factors.some((value) => value == null)) return false;
  const allowed = new Set([33, 50, 67]);
  return factors.every((value) => allowed.has(Math.round(value as number)));
};

const getQuadrantAffinities = (source: Partial<TeamMember> | InternalProfile): Record<QuadrantKey, number> => {
  const a = parseNumber(source.pi_factor_a) ?? 0;
  const b = parseNumber(source.pi_factor_b) ?? 0;
  const c = parseNumber(source.pi_factor_c) ?? 0;
  const d = parseNumber(source.pi_factor_d) ?? 0;

  const ia = (a + b) / 2;
  const rd = (a + d) / 2;
  const pp = (c + d) / 2;
  const te = (b + c) / 2;
  const total = ia + rd + pp + te;

  if (!Number.isFinite(total) || total <= 0) {
    return {
      innovation_agility: 25,
      results_discipline: 25,
      process_precision: 25,
      teamwork_experience: 25,
    };
  }

  return {
    innovation_agility: (ia / total) * 100,
    results_discipline: (rd / total) * 100,
    process_precision: (pp / total) * 100,
    teamwork_experience: (te / total) * 100,
  };
};

const getPrimaryQuadrantPlacement = (source: Partial<TeamMember> | InternalProfile): QuadrantKey | null => {
  const a = parseNumber(source.pi_factor_a);
  const b = parseNumber(source.pi_factor_b);
  const c = parseNumber(source.pi_factor_c);
  const d = parseNumber(source.pi_factor_d);
  if (a == null || b == null || c == null || d == null) return null;

  const acTotal = a + c;
  const bdTotal = b + d;
  const acPercentile = acTotal > 0 ? (a / acTotal) * 100 : 50;
  const bdPercentile = bdTotal > 0 ? (b / bdTotal) * 100 : 50;

  const highAC = acPercentile >= 50;
  const highBD = bdPercentile >= 50;

  if (highAC && highBD) return 'innovation_agility';
  if (highAC && !highBD) return 'results_discipline';
  if (!highAC && !highBD) return 'process_precision';
  return 'teamwork_experience';
};

const normalizeDiscoveryResult = (raw: any, fallbackMemberCount: number): TeamDiscoveryResult => {
  const memberPlacements = typeof raw?.member_placements === 'string'
    ? JSON.parse(raw.member_placements)
    : raw?.member_placements || [];
  const recommendations = typeof raw?.recommendations === 'string'
    ? JSON.parse(raw.recommendations)
    : raw?.recommendations || [];
  const teamBalancers = typeof raw?.team_balancers === 'string'
    ? JSON.parse(raw.team_balancers)
    : raw?.team_balancers || [];

  const quadrantScores = raw?.quadrant_scores
    ? {
      innovation_agility: toSafePercent(raw.quadrant_scores.innovation_agility),
      results_discipline: toSafePercent(raw.quadrant_scores.results_discipline),
      process_precision: toSafePercent(raw.quadrant_scores.process_precision),
      teamwork_experience: toSafePercent(raw.quadrant_scores.teamwork_experience),
    }
    : {
      innovation_agility: toSafePercent(raw?.innovation_agility_score ?? 25),
      results_discipline: toSafePercent(raw?.results_discipline_score ?? 25),
      process_precision: toSafePercent(raw?.process_precision_score ?? 25),
      teamwork_experience: toSafePercent(raw?.teamwork_experience_score ?? 25),
    };

  const memberCount = parseNumber(raw?.member_count);
  const skippedMembers = parseNumber(raw?.skipped_members);
  const alignmentScore = parseNumber(raw?.alignment_score);

  return {
    ...raw,
    member_placements: memberPlacements,
    recommendations,
    team_balancers: teamBalancers,
    quadrant_scores: quadrantScores,
    team_type: raw?.team_type,
    team_type_info: raw?.team_type_info || { name: raw?.team_type || 'Adapting', quadrants: [], tagline: '' },
    strategy_type: raw?.strategy_type,
    strategy_type_info: raw?.strategy_type_info || null,
    alignment_score: alignmentScore != null ? Math.round(alignmentScore) : null,
    member_count: memberCount != null ? memberCount : fallbackMemberCount,
    skipped_members: skippedMembers != null ? skippedMembers : 0,
  };
};

interface SuggestedCandidate {
  profile: InternalProfile;
  fitScore: number;
  weakestQuadrantLift: number;
  strongestQuadrant: QuadrantKey;
  strongestQuadrantScore: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

const TeamDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [team, setTeam] = useState<Team | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [discovery, setDiscovery] = useState<TeamDiscoveryResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [strategyType, setStrategyType] = useState<StrategyTypeKey | ''>('');
  const [discovering, setDiscovering] = useState(false);

  // Add member dialog
  const [addOpen, setAddOpen] = useState(false);
  const [newMember, setNewMember] = useState({
    first_name: '', last_name: '', email: '', role_title: '',
    pi_factor_a: '', pi_factor_b: '', pi_factor_c: '', pi_factor_d: '',
    reference_profile: '',
  });
  const [addingMember, setAddingMember] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [internalUsers, setInternalUsers] = useState<InternalProfile[]>([]);
  const [loadingInternalUsers, setLoadingInternalUsers] = useState(false);
  const [selectedInternalUserIds, setSelectedInternalUserIds] = useState<string[]>([]);
  const [internalUserSearch, setInternalUserSearch] = useState('');
  const [importingInternalMembers, setImportingInternalMembers] = useState(false);
  const [suggestedActionUserId, setSuggestedActionUserId] = useState<string | null>(null);

  const fetchTeam = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await teamsApi.getById(id);
      const loadedMembers = response.data.members || [];
      setTeam(response.data);
      setMembers(loadedMembers);
      if (response.data.discovery) {
        const normalized = normalizeDiscoveryResult(response.data.discovery, loadedMembers.length);
        setDiscovery(normalized);
        if (normalized.strategy_type) setStrategyType(normalized.strategy_type);
      }
    } catch (err: any) {
      showNotification(err.response?.data?.error || 'Failed to load team', 'error');
    } finally {
      setLoading(false);
    }
  }, [id, showNotification]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  const handleAddMember = async () => {
    if (!id || !newMember.first_name.trim() || !newMember.last_name.trim()) return;
    try {
      setAddingMember(true);
      await teamsApi.addMember(id, {
        first_name: newMember.first_name,
        last_name: newMember.last_name,
        email: newMember.email || undefined,
        role_title: newMember.role_title || undefined,
        pi_factor_a: newMember.pi_factor_a ? parseFloat(newMember.pi_factor_a) : undefined,
        pi_factor_b: newMember.pi_factor_b ? parseFloat(newMember.pi_factor_b) : undefined,
        pi_factor_c: newMember.pi_factor_c ? parseFloat(newMember.pi_factor_c) : undefined,
        pi_factor_d: newMember.pi_factor_d ? parseFloat(newMember.pi_factor_d) : undefined,
        reference_profile: newMember.reference_profile || undefined,
      } as any);
      showNotification('Member added', 'success');
      setAddOpen(false);
      setNewMember({ first_name: '', last_name: '', email: '', role_title: '', pi_factor_a: '', pi_factor_b: '', pi_factor_c: '', pi_factor_d: '', reference_profile: '' });
      fetchTeam();
    } catch (err: any) {
      showNotification(err.response?.data?.error || 'Failed to add member', 'error');
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!id) return;
    try {
      await teamsApi.removeMember(id, memberId);
      showNotification('Member removed', 'success');
      fetchTeam();
    } catch (err: any) {
      showNotification('Failed to remove member', 'error');
    }
  };

  const handleRunDiscovery = async () => {
    if (!id) return;
    try {
      setDiscovering(true);
      const response = await teamsApi.runDiscovery(id, strategyType || undefined);
      setDiscovery(normalizeDiscoveryResult(response.data, membersWithPI.length));
      setTabValue(1); // Switch to Discovery tab
      showNotification('Team Discovery analysis complete', 'success');
    } catch (err: any) {
      showNotification(err.response?.data?.error || 'Failed to run discovery', 'error');
    } finally {
      setDiscovering(false);
    }
  };

  const fetchInternalUsers = useCallback(async () => {
    try {
      setLoadingInternalUsers(true);
      const response = await usersApi.getInternalPeople();
      const profiles = response.data || [];
      setInternalUsers(profiles);
      setSelectedInternalUserIds((prev) =>
        prev.filter((userId) => profiles.some((profile) => profile.id === userId))
      );
    } catch (err: any) {
      showNotification(err.response?.data?.error || 'Failed to load internal people catalog', 'error');
    } finally {
      setLoadingInternalUsers(false);
    }
  }, [showNotification]);

  useEffect(() => {
    if (tabValue !== 1 || !discovery || loadingInternalUsers || internalUsers.length > 0) {
      return;
    }
    fetchInternalUsers();
  }, [tabValue, discovery, loadingInternalUsers, internalUsers.length, fetchInternalUsers]);

  const handleOpenImportInternal = async () => {
    setImportOpen(true);
    setInternalUserSearch('');
    setSelectedInternalUserIds([]);
    await fetchInternalUsers();
  };

  const handleToggleInternalUser = (userId: string) => {
    setSelectedInternalUserIds((prev) => (
      prev.includes(userId) ? prev.filter((idValue) => idValue !== userId) : [...prev, userId]
    ));
  };

  const membersWithPI = members.filter((member) => (
    hasCompletePiFactors(member) && !isLikelyEstimatedProfileScores(member)
  ));
  const membersWithEstimatedPi = members.filter((member) => (
    hasCompletePiFactors(member) && isLikelyEstimatedProfileScores(member)
  ));

  const existingTeamEmails = new Set(
    members
      .map((member) => member.email?.toLowerCase())
      .filter((email): email is string => !!email)
  );
  const existingTeamNameKeys = new Set(
    members
      .map((member) => `${member.first_name} ${member.last_name}`.trim().toLowerCase())
      .filter((nameKey) => !!nameKey)
  );
  const existingInternalPersonIds = new Set(
    members
      .map((member) => member.internal_person_id)
      .filter((idValue): idValue is number => typeof idValue === 'number')
  );

  const availableInternalUsers = internalUsers.filter((user) => {
    if (user.internal_person_id && existingInternalPersonIds.has(user.internal_person_id)) {
      return false;
    }
    if (user.email && existingTeamEmails.has(user.email.toLowerCase())) {
      return false;
    }
    if (!user.email && existingTeamNameKeys.has(user.name.trim().toLowerCase())) {
      return false;
    }
    return true;
  });

  const filteredInternalUsers = availableInternalUsers.filter((user) => {
    const query = internalUserSearch.trim().toLowerCase();
    if (!query) return true;

    return (
      user.name.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query) ||
      user.role_title.toLowerCase().includes(query) ||
      (user.reference_profile || '').toLowerCase().includes(query)
    );
  });

  const selectedFilteredCount = filteredInternalUsers.filter((user) => selectedInternalUserIds.includes(user.id)).length;
  const allFilteredSelected = filteredInternalUsers.length > 0 && selectedFilteredCount === filteredInternalUsers.length;
  const formatFactorDisplay = (member: TeamMember, value: unknown): string | null => {
    if (isLikelyEstimatedProfileScores(member)) return null;
    const parsed = parseNumber(value);
    if (parsed == null) return null;
    return String(Math.round(parsed));
  };

  const normalizedQuadrantScores = useMemo(() => {
    if (!discovery) return null;

    return {
      innovation_agility: toSafePercent(discovery.quadrant_scores?.innovation_agility),
      results_discipline: toSafePercent(discovery.quadrant_scores?.results_discipline),
      process_precision: toSafePercent(discovery.quadrant_scores?.process_precision),
      teamwork_experience: toSafePercent(discovery.quadrant_scores?.teamwork_experience),
    } as Record<QuadrantKey, number>;
  }, [discovery]);

  const weakestQuadrants = useMemo(() => {
    if (!normalizedQuadrantScores) return [] as QuadrantKey[];
    return [...QUADRANT_KEYS].sort((a, b) => normalizedQuadrantScores[a] - normalizedQuadrantScores[b]);
  }, [normalizedQuadrantScores]);

  const suggestedCandidates = useMemo(() => {
    if (!normalizedQuadrantScores || !discovery) return [] as SuggestedCandidate[];
    const weakestQuadrant = weakestQuadrants[0];
    if (!weakestQuadrant) return [] as SuggestedCandidate[];

    const deficits = QUADRANT_KEYS.reduce((acc, key) => {
      acc[key] = 100 - normalizedQuadrantScores[key];
      return acc;
    }, {} as Record<QuadrantKey, number>);
    const analyzedCount = Math.max(1, parseNumber(discovery.member_count) ?? membersWithPI.length);

    const ranked = availableInternalUsers
      .filter((profile) => hasCompletePiFactors(profile) && !isLikelyEstimatedProfileScores(profile))
      .map((profile) => {
        const affinities = getQuadrantAffinities(profile);
        const primaryQuadrant = getPrimaryQuadrantPlacement(profile) || QUADRANT_KEYS.reduce((best, key) => (
          affinities[key] > affinities[best] ? key : best
        ), QUADRANT_KEYS[0]);
        const fitScore = deficits[primaryQuadrant];
        const projectedWeakest = (
          (normalizedQuadrantScores[weakestQuadrant] * analyzedCount) +
          (primaryQuadrant === weakestQuadrant ? 100 : 0)
        ) / (analyzedCount + 1);
        const strongestQuadrant = primaryQuadrant;

        return {
          profile,
          fitScore,
          weakestQuadrantLift: projectedWeakest - normalizedQuadrantScores[weakestQuadrant],
          strongestQuadrant,
          strongestQuadrantScore: affinities[strongestQuadrant],
        };
      })
      .sort((a, b) => {
        if (b.fitScore !== a.fitScore) {
          return b.fitScore - a.fitScore;
        }
        return b.weakestQuadrantLift - a.weakestQuadrantLift;
      });

    return ranked.slice(0, 8);
  }, [availableInternalUsers, discovery, membersWithPI.length, normalizedQuadrantScores, weakestQuadrants]);

  const handleToggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      const filteredIds = new Set(filteredInternalUsers.map((user) => user.id));
      setSelectedInternalUserIds((prev) => prev.filter((userId) => !filteredIds.has(userId)));
      return;
    }

    setSelectedInternalUserIds((prev) => {
      const next = new Set(prev);
      filteredInternalUsers.forEach((user) => next.add(user.id));
      return Array.from(next);
    });
  };

  const handleImportInternalMembers = async () => {
    if (!id || selectedInternalUserIds.length === 0) return;

    try {
      setImportingInternalMembers(true);

      const usersById = new Map(internalUsers.map((user) => [user.id, user]));
      const selectedProfiles = selectedInternalUserIds
        .map((userId) => usersById.get(userId))
        .filter((user): user is InternalProfile => !!user);

      if (selectedProfiles.length === 0) {
        showNotification('No valid internal people selected', 'error');
        return;
      }

      const resolvedResponse = await usersApi.resolveInternalProfilesMembers(selectedProfiles);
      const rows = resolvedResponse.data?.members || [];
      const withPiScores = resolvedResponse.data?.with_pi_scores || 0;

      if (rows.length === 0) {
        showNotification('No valid team members were resolved from the selected employees', 'error');
        return;
      }

      await teamsApi.addMembersBulk(id, rows);
      const scoreSummary = withPiScores > 0 ? ` (${withPiScores} with PI scores)` : '';
      showNotification(`Added ${rows.length} team member${rows.length === 1 ? '' : 's'} from internal catalog${scoreSummary}`, 'success');
      setImportOpen(false);
      setSelectedInternalUserIds([]);
      await fetchTeam();
    } catch (err: any) {
      showNotification(err.response?.data?.error || 'Failed to add internal people to team', 'error');
    } finally {
      setImportingInternalMembers(false);
    }
  };

  const handleAddSuggestedMember = async (profile: InternalProfile) => {
    if (!id) return;

    try {
      setSuggestedActionUserId(profile.id);
      setDiscovering(true);

      const resolvedResponse = await usersApi.resolveInternalProfilesMembers([profile]);
      const resolvedMember = resolvedResponse.data?.members?.[0];
      if (!resolvedMember) {
        showNotification('Unable to resolve this profile into a team member row', 'error');
        return;
      }

      await teamsApi.addMember(id, resolvedMember);
      const discoveryResponse = await teamsApi.runDiscovery(id, strategyType || undefined);
      setDiscovery(normalizeDiscoveryResult(discoveryResponse.data, membersWithPI.length + 1));
      await fetchTeam();
      showNotification(`Added ${profile.name} and reran Team Discovery`, 'success');
    } catch (err: any) {
      showNotification(err.response?.data?.error || 'Failed to add suggested member', 'error');
    } finally {
      setSuggestedActionUserId(null);
      setDiscovering(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!team) {
    return <Alert severity="error">Team not found</Alert>;
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <IconButton onClick={() => navigate('/teams')} size="small">
          <ArrowBack />
        </IconButton>
        <Groups color="primary" />
        <Typography variant="h4">{team.name}</Typography>
        {team.department && <Chip label={team.department} size="small" variant="outlined" />}
      </Box>
      {team.description && (
        <Typography variant="body2" color="text.secondary" sx={{ ml: 6, mb: 2 }}>
          {team.description}
        </Typography>
      )}

      {/* Tabs */}
      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 1 }}>
        <Tab label={`Members (${members.length})`} />
        <Tab label="Team Discovery" disabled={membersWithPI.length === 0} />
      </Tabs>

      {/* Members Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Team Members</Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<PersonAdd />}
              onClick={handleOpenImportInternal}
              size="small"
            >
              Add From Internal People
            </Button>
            <Button variant="outlined" startIcon={<Add />} onClick={() => setAddOpen(true)} size="small">
              Add Member
            </Button>
          </Stack>
        </Box>

        {members.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No members yet. Add team members with their PI factor scores.</Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="center">A (Dom)</TableCell>
                  <TableCell align="center">B (Ext)</TableCell>
                  <TableCell align="center">C (Pat)</TableCell>
                  <TableCell align="center">D (Form)</TableCell>
                  <TableCell>Profile</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="body2" fontWeight={500}>
                          {member.first_name} {member.last_name}
                        </Typography>
                        {(member as any).pi_person_id && (
                          <a
                            href={`https://app.predictiveindex.com/pi/inspire/client/person/${(member as any).pi_person_id}/about`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: 'flex', alignItems: 'center', color: 'inherit', opacity: 0.5 }}
                          >
                            <OpenInNew sx={{ fontSize: 14 }} />
                          </a>
                        )}
                      </Box>
                      {member.email && (
                        <Typography variant="caption" color="text.secondary">{member.email}</Typography>
                      )}
                    </TableCell>
                    <TableCell>{member.role_title || '-'}</TableCell>
                    <TableCell align="center">
                      {formatFactorDisplay(member, member.pi_factor_a) ?? <Chip label="?" size="small" color="warning" />}
                    </TableCell>
                    <TableCell align="center">
                      {formatFactorDisplay(member, member.pi_factor_b) ?? <Chip label="?" size="small" color="warning" />}
                    </TableCell>
                    <TableCell align="center">
                      {formatFactorDisplay(member, member.pi_factor_c) ?? <Chip label="?" size="small" color="warning" />}
                    </TableCell>
                    <TableCell align="center">
                      {formatFactorDisplay(member, member.pi_factor_d) ?? <Chip label="?" size="small" color="warning" />}
                    </TableCell>
                    <TableCell>{member.reference_profile || '-'}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleRemoveMember(member.id)} color="error">
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {members.length > 0 && (
          <Paper sx={{ p: 2, mt: 2 }}>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Strategy Type (optional)</InputLabel>
                <Select
                  value={strategyType}
                  label="Strategy Type (optional)"
                  onChange={(e) => setStrategyType(e.target.value as StrategyTypeKey)}
                >
                  <MenuItem value="">None</MenuItem>
                  {STRATEGY_OPTIONS.map(opt => (
                    <MenuItem key={opt.key} value={opt.key}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={discovering ? <CircularProgress size={18} color="inherit" /> : <PlayArrow />}
                onClick={handleRunDiscovery}
                disabled={discovering || membersWithPI.length === 0}
              >
                {discovering ? 'Analyzing...' : 'Run Team Discovery'}
              </Button>
              {membersWithPI.length < members.length && (
                <Typography variant="caption" color="warning.main">
                  {members.length - membersWithPI.length} member(s) missing reliable PI data will be skipped
                  {membersWithEstimatedPi.length > 0 && ` (${membersWithEstimatedPi.length} profile-estimated)`}
                </Typography>
              )}
            </Box>
          </Paper>
        )}
      </TabPanel>

      {/* Discovery Tab */}
      <TabPanel value={tabValue} index={1}>
        {!discovery ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary" gutterBottom>
              No discovery results yet. Add members with PI data and run Team Discovery.
            </Typography>
            <Button variant="contained" onClick={() => setTabValue(0)}>
              Go to Members
            </Button>
          </Paper>
        ) : (
          <Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '440px 1fr' }, gap: 3 }}>
              {/* Left: Team Map */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Team Map</Typography>
                <TeamMap
                  memberPlacements={discovery.member_placements}
                  quadrantScores={discovery.quadrant_scores}
                  size={400}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {discovery.member_count} member{discovery.member_count !== 1 ? 's' : ''} analyzed
                  {discovery.skipped_members > 0 && ` (${discovery.skipped_members} skipped)`}
                </Typography>
              </Paper>

              {/* Right: Team Type, Strategy, Recommendations */}
              <TeamTypeCard result={discovery} />
            </Box>

            <Paper sx={{ p: 2.5, mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6">Potential Members To Fill Gaps</Typography>
                {loadingInternalUsers && <CircularProgress size={18} />}
              </Box>

              {weakestQuadrants.length > 0 && normalizedQuadrantScores && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Weakest areas right now:
                  {' '}
                  {QUADRANT_LABELS[weakestQuadrants[0]]} ({Math.round(normalizedQuadrantScores[weakestQuadrants[0]])}%)
                  {weakestQuadrants[1] && (
                    <>
                      {' '}and {QUADRANT_LABELS[weakestQuadrants[1]]} ({Math.round(normalizedQuadrantScores[weakestQuadrants[1]])}%)
                    </>
                  )}
                  .
                </Typography>
              )}

              {loadingInternalUsers ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={26} />
                </Box>
              ) : suggestedCandidates.length === 0 ? (
                <Alert severity="info">
                  No suggested internal people with complete PI scores are available right now.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Best Coverage</TableCell>
                        <TableCell>Gap Fit</TableCell>
                        <TableCell>Weakest-Area Lift</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {suggestedCandidates.map((candidate) => (
                        <TableRow key={candidate.profile.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {candidate.profile.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {candidate.profile.email}
                            </Typography>
                          </TableCell>
                          <TableCell>{candidate.profile.role_title || '-'}</TableCell>
                          <TableCell>
                            {QUADRANT_LABELS[candidate.strongestQuadrant]} ({Math.round(candidate.strongestQuadrantScore)}%)
                          </TableCell>
                          <TableCell>{Math.round(candidate.fitScore)}%</TableCell>
                          <TableCell>
                            {`${candidate.weakestQuadrantLift >= 0 ? '+' : ''}${candidate.weakestQuadrantLift.toFixed(1)}%`}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleAddSuggestedMember(candidate.profile)}
                              disabled={discovering || suggestedActionUserId === candidate.profile.id}
                            >
                              {suggestedActionUserId === candidate.profile.id ? 'Adding...' : 'Add + Rerun'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Box>
        )}
      </TabPanel>

      {/* Add Internal People Dialog */}
      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Team Members from Internal People</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Search by name, email, title, or profile"
              value={internalUserSearch}
              onChange={(e) => setInternalUserSearch(e.target.value)}
              fullWidth
            />

            {loadingInternalUsers ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={28} />
              </Box>
            ) : availableInternalUsers.length === 0 ? (
              <Alert severity="info">No internal people available to add. Sync from the Teams page first, or everyone is already on this team.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allFilteredSelected}
                          indeterminate={selectedFilteredCount > 0 && !allFilteredSelected}
                          onChange={handleToggleSelectAllFiltered}
                          inputProps={{ 'aria-label': 'select all filtered employees' }}
                        />
                      </TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Profile</TableCell>
                      <TableCell>Assessment</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredInternalUsers.map((user) => (
                      <TableRow
                        key={user.id}
                        hover
                        onClick={() => handleToggleInternalUser(user.id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedInternalUserIds.includes(user.id)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => handleToggleInternalUser(user.id)}
                          />
                        </TableCell>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          {user.role_title || '-'}
                        </TableCell>
                        <TableCell>
                          {user.reference_profile ? <Chip size="small" label={user.reference_profile} /> : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={user.has_completed_behavioral_assessment ? 'success' : 'default'}
                            label={user.has_completed_behavioral_assessment ? 'Complete' : 'Pending'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredInternalUsers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography variant="body2" color="text.secondary">
                            No employees match your search.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Typography variant="caption" color="text.secondary">
              {selectedInternalUserIds.length} selected
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleImportInternalMembers}
            disabled={loadingInternalUsers || importingInternalMembers || selectedInternalUserIds.length === 0}
          >
            {importingInternalMembers ? 'Adding...' : `Add ${selectedInternalUserIds.length} Member${selectedInternalUserIds.length === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Team Member</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box display="flex" gap={2}>
              <TextField
                label="First Name"
                value={newMember.first_name}
                onChange={(e) => setNewMember({ ...newMember, first_name: e.target.value })}
                fullWidth required autoFocus
              />
              <TextField
                label="Last Name"
                value={newMember.last_name}
                onChange={(e) => setNewMember({ ...newMember, last_name: e.target.value })}
                fullWidth required
              />
            </Box>
            <Box display="flex" gap={2}>
              <TextField
                label="Email"
                value={newMember.email}
                onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                fullWidth
              />
              <TextField
                label="Job Title / Role"
                value={newMember.role_title}
                onChange={(e) => setNewMember({ ...newMember, role_title: e.target.value })}
                fullWidth
              />
            </Box>
            <Typography variant="subtitle2" color="text.secondary">PI Factor Scores (0-100)</Typography>
            <Box display="flex" gap={2}>
              <TextField
                label="A (Dominance)"
                value={newMember.pi_factor_a}
                onChange={(e) => setNewMember({ ...newMember, pi_factor_a: e.target.value })}
                type="number" inputProps={{ min: 0, max: 100 }} fullWidth
              />
              <TextField
                label="B (Extraversion)"
                value={newMember.pi_factor_b}
                onChange={(e) => setNewMember({ ...newMember, pi_factor_b: e.target.value })}
                type="number" inputProps={{ min: 0, max: 100 }} fullWidth
              />
            </Box>
            <Box display="flex" gap={2}>
              <TextField
                label="C (Patience)"
                value={newMember.pi_factor_c}
                onChange={(e) => setNewMember({ ...newMember, pi_factor_c: e.target.value })}
                type="number" inputProps={{ min: 0, max: 100 }} fullWidth
              />
              <TextField
                label="D (Formality)"
                value={newMember.pi_factor_d}
                onChange={(e) => setNewMember({ ...newMember, pi_factor_d: e.target.value })}
                type="number" inputProps={{ min: 0, max: 100 }} fullWidth
              />
            </Box>
            <TextField
              label="Reference Profile (optional)"
              value={newMember.reference_profile}
              onChange={(e) => setNewMember({ ...newMember, reference_profile: e.target.value })}
              placeholder="e.g., Captain, Maverick, Analyzer"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddMember}
            variant="contained"
            disabled={addingMember || !newMember.first_name.trim() || !newMember.last_name.trim()}
          >
            {addingMember ? 'Adding...' : 'Add Member'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamDetail;
